import { useEffect, useState } from "react";
import {
  PieChart, Pie, Cell,
  LineChart, Line,
  XAxis, YAxis, Tooltip,
  ResponsiveContainer,
  BarChart, Bar
} from "recharts";

/* ================= CI ================= */
const CI = "#00bfa5";
const CI_SOFT = "#e6f7f4";
const CI_DARK = "#009e8a";

const PIE_COLORS = [
  CI,
  "#3b82f6",
  "#f59e0b",
  "#ef4444"
];

export default function Dashboard() {
  const today = new Date().toISOString().slice(0, 10);

  const [filters, setFilters] = useState({
    date: today,
    store_id: "all"
  });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [livePulse, setLivePulse] = useState(false);

  const fetchData = async (isSilent = false) => {
    if (!isSilent) {
      setInitialLoading(true); // ใช้เฉพาะโหลดแรก
    }
    const token = localStorage.getItem("token")

    const params = new URLSearchParams({ date: filters.date });
    if (filters.store_id !== "all") {
      params.append("store_id", filters.store_id);
    }

    const res = await fetch(`/api/dashboard/summary?${params}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();

    setData(json.data);

    if (!isSilent) {
      setInitialLoading(false);
    }
  };

  /* ✅ โหลดครั้งแรก */
  useEffect(() => {
    const token = localStorage.getItem("token")
    console.log("Token:", token) // ✅ ตรวจสอบ token ใน console
    if (!token) {
      window.location.href = "/login"
    }

    fetchData(false); // ❗ โหลดจริง
  }, []);

  /* ✅ SSE = refresh เท่านั้น */
  useEffect(() => {
    const sse = new EventSource("/api/dashboard/stream");
    let debounce;

    sse.onmessage = () => {
      clearTimeout(debounce);

      setIsRefreshing(true);

      debounce = setTimeout(async () => {
        await fetchData(true); // ✅ silent refresh
        setTimeout(() => setIsRefreshing(false), 700);
      }, 300);
    };

    return () => {
      sse.close();
      clearTimeout(debounce);
    };
  }, []);

  if (initialLoading || !data) {
    return <div className="p-6">Loading dashboard...</div>;
  }

  return (
    <div
      className="
        p-4 md:p-6 space-y-6 min-h-screen
        transition-colors duration-700
      "
      style={{
        background: isRefreshing
          ? "linear-gradient(180deg, #e6f7f4, #f9fafb)"
          : "#f9fafb"
      }}
    >

      <div className="
        fixed bottom-4 right-4
        flex items-center gap-2
        bg-white shadow
        px-3 py-1 rounded-full
        text-xs text-gray-600
      ">
        <span className={`w-2 h-2 rounded-full ${
          isRefreshing ? "bg-emerald-500 animate-pulse" : "bg-gray-300"
        }`} />
        Live
      </div>


      {/* ================= FILTER ================= */}
      <div className="
        bg-white rounded-xl shadow p-4
        grid grid-cols-1 sm:grid-cols-2 md:flex
        gap-3 md:items-end
      ">
        <div className="w-full md:w-auto">
          <div className="text-xs text-gray-500 mb-1">วันที่</div>
          <input
            type="date"
            className="border rounded px-3 py-2 text-sm w-full focus:ring-2"
            style={{ outlineColor: CI }}
            value={filters.date}
            onChange={e => setFilters({ ...filters, date: e.target.value })}
          />
        </div>

        <div className="w-full md:w-auto">
          <div className="text-xs text-gray-500 mb-1">สาขา</div>
          <select
            className="border rounded px-3 py-2 text-sm w-full focus:ring-2"
            style={{ outlineColor: CI }}
            value={filters.store_id}
            onChange={e => setFilters({ ...filters, store_id: e.target.value })}
          >
            <option value="all">ทุกสาขา</option>
            {data.stores.map(store => (
              <option key={store.store_id} value={store.store_id}>
                {store.store_id} - {store.device_name}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={fetchData}
          className="
            text-white rounded
            px-4 py-2 text-sm
            w-full md:w-auto
          "
          style={{ background: CI }}
          onMouseOver={e => e.currentTarget.style.background = CI_DARK}
          onMouseOut={e => e.currentTarget.style.background = CI}
        >
          ค้นหา
        </button>
      </div>

      {/* ================= FLOW ================= */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
        <Stat title="Pass-by (ครั้ง)" value={data.passby} />
        <Stat title="เข้าร้าน (ครั้ง)" value={data.entry} />
        <Stat
          title="Conversion Rate"
          value={`${data.conversion_rate}%`}
          highlight
        />
      </div>
      {/* ================= PEOPLE ================= */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
        <Stat title="ลูกค้า VIP (คน)" value={data.vip} />
        <Stat title="ลูกค้าสมาชิก (คน)" value={data.member} />
        <Stat title="พนักงาน (คน)" value={data.staff} />
        <Stat title="Returning (คน)" value={data.returning} />
      </div>

      {/* ================= OVERVIEW ================= */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* PIE */}
        <div className="bg-white rounded-xl shadow p-4 h-64 sm:h-72">
          <h3 className="font-semibold mb-2 text-sm md:text-base">
            สัดส่วนลูกค้า
          </h3>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data.pie}
                dataKey="value"
                nameKey="label"
                innerRadius={45}
                outerRadius={70}
              >
                {data.pie.map((_, i) => (
                  <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* BAR TODAY */}
        <div className="bg-white rounded-xl shadow p-4 h-64 sm:h-72">
          <h3 className="font-semibold mb-2 text-sm md:text-base">
            Pass-by vs เข้าร้าน (วันนี้)
          </h3>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={[
                { label: "Pass-by", value: data.passby },
                { label: "เข้าร้าน", value: data.entry }
              ]}
            >
              <XAxis dataKey="label" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar
                dataKey="value"
                fill={CI}
                radius={[6, 6, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

      </div>

      {/* ================= TREND ================= */}
      <div className="bg-white rounded-xl shadow p-4 h-72 md:h-80">
        <h3 className="font-semibold mb-2 text-sm md:text-base">
          Pass-by vs Entry (7 วัน)
        </h3>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data.trend}>
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Line
              dataKey="passby"
              stroke="#cbd5e1"
              strokeWidth={2}
            />
            <Line
              dataKey="entry"
              stroke={CI}
              strokeWidth={3}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* ================= INSIGHT ================= */}
      <div className="bg-white rounded-xl shadow p-4 md:p-6">
        <h3 className="font-semibold mb-2 text-sm md:text-base">
          Insight
        </h3>
        {data.insight.length === 0 ? (
          <p className="text-gray-400 text-sm">ไม่มี insight</p>
        ) : (
          <ul className="text-sm space-y-1">
            {data.insight.map((i, idx) => (
              <li key={idx} className="flex gap-2">
                <span style={{ color: CI }}>●</span>
                {i}
              </li>
            ))}
          </ul>
        )}
      </div>

    </div>
  );
}

/* ================= STAT ================= */
function Stat({ title, value, highlight }) {
  return (
    <div
      className={`
        bg-white rounded-xl shadow
        p-3 md:p-4
        ${highlight ? "ring-2" : ""}
      `}
      style={highlight ? { ringColor: CI, borderColor: CI } : {}}
    >
      <div className="text-xs md:text-sm text-gray-500 truncate">
        {title}
      </div>
      <div className="text-lg md:text-2xl font-bold mt-1">
        {value}
      </div>
    </div>
  );
}
